import React, {useState} from "react";
import Table from "@amzn/awsui-components-react/polaris/table";
import Header from "@amzn/awsui-components-react/polaris/header";
import Box from "@amzn/awsui-components-react/polaris/box";
import { SpaceBetween, Pagination, Spinner } from "@amzn/awsui-components-react/polaris";
import { getExcaliburAuditData, downloadErrorReportByTransactionId } from "./components/excaliburAPIs";
import {useReducer} from "react";
import { useNavigate } from "react-router-dom";
import {INITIAL_STATE, tableReducer, tableReducerInit} from "./components/excaliburTableReducer";
import {
    getFileNameButtonFromS3Key,
    getFileNameButtonFromDMS,
    statusColorCodes
} from "../../common/utils/commonUtils";
import Button from "@amzn/awsui-components-react/polaris/button";
import Link from "@amzn/awsui-components-react/polaris/link";
import { ROUTE_PATH_FILING_PAGE } from "../../common/constants/route_paths";
import Modal from "@amzn/awsui-components-react/polaris/modal";
import {formatDateWithTime} from "../../common/utils/apiUtils";
import {uploadFiles, downloadFolder} from "../../common/utils/s3Utils";
import AppConfig from "../../AppConfig";
import { ARTIFACTS_FORMAT } from "../../common/constants/constants";
import { EXCALIBUR_STATUS, ERROR_REPORT_DOWNLOAD_FAILURE_FLASH } from "./constants/constants";

const Actiontable = (params) => {
    const {downloadDocumentDispatcher} = params;

    //Table State Management Reducer
    const [table, tableDispatcher] = useReducer(tableReducer,
        INITIAL_STATE, tableReducerInit);
    const navigate = useNavigate();     

    //This is to initialize the first call
    if(table.initialize){
        getExcaliburAuditData(tableDispatcher, table.lastEvaluatedKey, 'Excalibur');
    }

    const [selectedItems, setSelectedItems] = useState([]);
    const [visible, setVisible] = useState(false);
    const [artifactLoading, setArtifactLoading] = useState(false);
    const [artifactFlash, setArtifactFlash] = useState(null);
    const [artifactKeysMap, setArtifactKeysMap] = useState({});

    const getFolderPath = (key) => {
        if (!key) return key;
        // Remove trailing slash if present
        const trimmedKey = key.endsWith('/') ? key.slice(0, -1) : key;
        // If key contains a dot after the last slash, it's a file path — extract the folder
        const lastSlashIndex = trimmedKey.lastIndexOf('/');
        const lastSegment = trimmedKey.substring(lastSlashIndex + 1);
        if (lastSegment.includes('.')) {
            return lastSlashIndex >= 0 ? trimmedKey.substring(0, lastSlashIndex) : trimmedKey;
        }
        return trimmedKey;
    }

    const downloadArtifacts = async (generatedFileKey) => {
        const folderKey = getFolderPath(generatedFileKey);
        setArtifactLoading(true);
        setArtifactFlash(null);
        try {
            await downloadFolder(folderKey, AppConfig[params.domain].ARTIFACTS_BUCKET, params.domain);
        } catch (error) {
            console.error("Error downloading artifacts:", error);
            setArtifactFlash({type: "error", message: "Error occurred while downloading artifacts"});
        }
        setArtifactLoading(false);
    }

    const uploadArtifacts = async (item, files) => {
        const folderKey = getFolderPath(item.generatedFileKey);
        setArtifactLoading(true);
        setArtifactFlash(null);
        try {
            await uploadFiles(files, AppConfig[params.domain].ARTIFACTS_BUCKET, folderKey, params.domain);

            // Track uploaded artifact S3 keys locally by transactionId
            const newKeys = Array.from(files).map(file => `${folderKey}/${file.name}`);
            setArtifactKeysMap(prev => ({
                ...prev,
                [item.transactionId]: [...(prev[item.transactionId] || []), ...newKeys]
            }));

            setArtifactFlash({type: "success", message: "Artifacts uploaded successfully"});
        } catch (error) {
            console.error("Error uploading artifacts:", error);
            setArtifactFlash({type: "error", message: "Error occurred while uploading artifacts"});
        }
        setArtifactLoading(false);
    }

    const onDownloadErrorReport = async (row) => {
        setArtifactFlash(null);
        try {
            await downloadErrorReportByTransactionId({
                useCase: row.useCase,
                subUseCase: row.subUseCase,
                transactionId: row.transactionId,
            });
        } catch (error) {
            console.error("Error downloading error report:", error);
            setArtifactFlash(ERROR_REPORT_DOWNLOAD_FAILURE_FLASH);
        }
    }

    const renderErrorDetails = (row) => {
        const hasErrorReport = row.status === EXCALIBUR_STATUS.FAILURE && Boolean(row.generatedFileKey);
        if (!hasErrorReport) {
            return row.errorDetails;
        }
        return (
            <SpaceBetween direction="horizontal" size="xs">
                <span>{row.errorDetails}</span>
                <span>-</span>
                <Link onFollow={() => onDownloadErrorReport(row)}>
                    Download error report
                </Link>
            </SpaceBetween>
        );
    }

    const onSelectionChange = (item) => {
        setSelectedItems(item.detail.selectedItems)
    }

    const handleEFileClick = () => {
        setVisible(true);
    }
    const handleDismissModal = () => {
        setVisible(false);
    }
    const navigateToFiling = () => {
        const selected = selectedItems[0];
        navigate(ROUTE_PATH_FILING_PAGE, {
            state: {
                data: {
                    ...selected,
                    artifactKeys: artifactKeysMap[selected.transactionId] || [],
                    artifactBucket: AppConfig[params.domain].ARTIFACTS_BUCKET
                }
            }
        });
    }

    return (
        <>
        {artifactLoading && <Spinner size="large" />}
        {artifactFlash &&
            <Box margin={{bottom: "s"}}>
                <div style={{color: artifactFlash.type === "error" ? "red" : "green"}}>
                    {artifactFlash.message}
                </div>
            </Box>
        }
        <Modal
            onDismiss={handleDismissModal}
            visible={visible}
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link">Cancel</Button>
                        <Button variant="primary" onClick={navigateToFiling}>Ok</Button>
                    </SpaceBetween>
                </Box>
            }
            header="Modal title"
        >
            Proceed To Filing?
        </Modal>
        <Table
            header={<Header
                variant="h2"
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button disabled={selectedItems.length === 0 || selectedItems[0].status !== "SUCCESS"} onClick={handleEFileClick}>
                        Start E-File
                    </Button>
                  </SpaceBetween>
                }
              >Submitted Requests
              </Header>}
            loading={table.loading}
            selectionType="single"
            onSelectionChange={onSelectionChange}
            selectedItems={selectedItems}
            loadingText={table.loadingText}
            onSortingChange={((event)=>{
                    tableDispatcher({type:"SORTING_CHANGED",
                        payload:{isDescending:event.detail.isDescending,
                            sortingField:event.detail.sortingColumn.sortingField}});
            })}
            sortingColumn={table.sortingColumn}
            sortingDescending={table.sortingDescending}
            columnDefinitions={[
                {
                    id: "useCase",
                    header: "Country",
                    cell: e => e.useCase,
                    sortingField: "useCase"
                },
                {
                    id: "subUseCase",
                    header: "Filing Type",
                    cell: e => e.subUseCase,
                    sortingField: "subUseCase"

                },
                {
                    id: "status",
                    header: "Status",
                    cell: e => {
                        return (<div style={statusColorCodes(e.status)}>{e.status}</div>)
                    },
                    sortingField: "status"

                },
                {
                    id: "startTimestamp",
                    header: "Request Created on",
                    cell: e => {return formatDateWithTime(e.startTimestamp)},
                    sortingField: "startTimestamp"
                },
                {
                    id: "createdBy",
                    header: "Submitted By",
                    cell: e => e.createdBy,
                    sortingField: "createdBy"
                },
                {
                    id: "inputFileKey",
                    header: "Input File",
                    cell: e => {
                        return getFileNameButtonFromS3Key(e.inputFileKey, 'EXCALIBUR_BUCKET',params.domain)
                    },
                    sortingField: "inputFileKey"
                },
                {
                    id: "generatedFileKey",
                    header: "Output File",
                    cell: e => {
                        return getFileNameButtonFromDMS(e.documentId, e.useCase, e.subUseCase, e.inputFileKey, downloadDocumentDispatcher);
                    },
                },
                {
                    id: "errorDetails",
                    header: "Error Details",
                    cell: row => renderErrorDetails(row),
                },
                {
                    id: "txnArtifacts",
                    header: "Artifacts",
                    cell: e => {
                        return (
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button iconName="download" onClick={() => downloadArtifacts(e.generatedFileKey)}>View</Button>
                                <Button iconName="upload" variant="primary">
                                    <input className="inputButton hidden" type={"file"} accept={ARTIFACTS_FORMAT}
                                           name="file" onChange={(event) => uploadArtifacts(e, event.target.files)}/>
                                    Upload
                                </Button>
                            </SpaceBetween>
                        )
                    },
                    minWidth: 300
                }
            ]}
            visibleColumns={[
                "useCase",
                "subUseCase",
                "status",
                "startTimestamp",
                "createdBy",
                "inputFileKey",
                "generatedFileKey",
                "errorDetails",
                "txnArtifacts"
            ]}
            empty={
                <Box textAlign="center" color="inherit">
                    <b>No files</b>
                    <Box
                        padding={{ bottom: "s" }}
                        variant="p"
                        color="inherit">
                        No files to display.
                    </Box>

                </Box>
            }
            items={table.cachedData.get(table.currentPageIndex)}
            pagination={
                <Pagination
                    currentPageIndex={table.currentPageIndex}
                    pagesCount={table.pageCount}
                    onChange={event => {
                        tableDispatcher({type:"PAGE_INDEX_CHANGED",
                            payload:{pageIndex:event.detail.currentPageIndex, dispatcher:tableDispatcher}})
                        console.log(table.cachedData);
                    }
                    }
                    ariaLabels={{
                        nextPageLabel: "Next page",
                        previousPageLabel: "Previous page",
                        pageLabel: pageNumber =>
                            `Page ${pageNumber} of all pages`
                    }}
                />
            }
            preferences={
                <Button iconName="refresh" variant="icon"
                    onClick={()=>{
                        tableDispatcher({type:"REFRESH",payload:{dispatcher:tableDispatcher}})
                    }}
                />
            }
        />
        </>
    );
}

export default Actiontable;